import React, { useState, useEffect, useCallback, useRef } from 'react';
import cx from 'classnames';
import moment from 'moment-timezone';
import find from 'lodash/find';

import {Button, Popover} from "@nextiva/next-ui";
import presets, { sortUnits } from '../constants/presets';
import Styles from './DrpPopover.module.scss';
import CustomOptions from './CustomOptions/CustomOptions';
import RelativeOptions from './RelativeOptions/RelativeOptions';

const DrpPopover = ({
  className,
  bodyClassName,
  children,
  dateRange,
  timezone,
  isPeriod,
  isRealTime,
  isOpen: propsIsOpen = false,
  relativeUnits = sortUnits,
  presets: propPresets = presets,
  disabled,
  onChangeCancel,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(propsIsOpen);

  useEffect(() => {
    setIsOpen(Boolean(propsIsOpen));
  }, [propsIsOpen]);

  const togglePopover = useCallback(() => {
    if (!disabled) {
      setIsOpen(prev => !prev);
    }
  }, [disabled]);

  const bodyRef = useRef();

  return (
    <Popover
      bodyClassName={cx(Styles['date-range-picker'], bodyClassName)}
      className={cx("drp-popover", Styles['drp-popover'], className)}
      body={
        <DrpPopoverBody
          ref={bodyRef}
          togglePopover={togglePopover}
          dateRange={dateRange}
          timezone={timezone}
          isPeriod={isPeriod}
          isRealTime={isRealTime}
          relativeUnits={relativeUnits}
          presets={propPresets}
          onChangeCancel={onChangeCancel}
          onChange={onChange}
        />
      }
      isOpen={isOpen}
      togglePopover={togglePopover}
      clickOutsideIgnoreClasses={['react-datepicker']}
    >
      {children}
    </Popover>
  );
};

const DrpPopoverBody = React.forwardRef(({
  className,
  dateRange: initialDateRange,
  timezone,
  isPeriod,
  isRealTime,
  onChangeCancel,
  relativeUnits,
  presets,
  onChange,
  togglePopover,
}, ref) => {
  const getFullDateRange = useCallback((range) => {
    return range.type === 'relative'
      ? range
      : {
          type: 'absolute',
          ...range,
        };
  }, []);

  const [dateRange, setDateRange] = useState(getFullDateRange(initialDateRange));

  useEffect(() => {
    setDateRange(getFullDateRange(initialDateRange));
  }, [initialDateRange, getFullDateRange]);

  const findFirstPreset = useCallback((presetsList, units) => {
    let first = presetsList.Today;
    units.some(unit => {
      const found = find(presetsList, { unit });
      if (found) {
        first = found;
        return true;
      }
      return false;
    });
    return first;
  }, []);

  const handleChangeDateRangeType = type => () => {
    if (type === dateRange.type) return;

    if (type === 'relative') {
      setDateRange({
        type: 'relative',
        ...findFirstPreset(presets, relativeUnits),
      });
    } else {
      setDateRange({
        type: 'absolute',
        start: moment.tz(timezone).startOf('day').toISOString(),
        end: moment.tz(timezone).toISOString(),
      });
    }
  };

  const handleChange = (updatedRange) => {
    if(onChange) onChange(updatedRange);
    togglePopover();
  };

  return (
    <div className={cx(className, "drp-popover__body", Styles['drp-popover__body'])} ref={ref}>
      {!isPeriod && (
        <div className={cx("drp-popover__buttons", Styles['drp-popover__buttons'])}>
          <Button
            className={cx("drp-popover__relative-button", "drp-popover__button", Styles['drp-popover__button'], {
              'drp-popover__button--active': dateRange.type === 'relative',
            })}
            onClick={handleChangeDateRangeType('relative')}
            data-testid="AnalyticsSpa-DrpPopover-RelativeButton"
          >
            Relative
          </Button>
          <Button
            className={cx("drp-popover__custom-button", "drp-popover__button", Styles['drp-popover__button'], {
              'drp-popover__button--active': dateRange.type === 'absolute',
            })}
            onClick={handleChangeDateRangeType('absolute')}
            data-testid="AnalyticsSpa-DrpPopover-CustomButton"
          >
            Custom
          </Button>
        </div>
      )}
      {dateRange.type === 'relative' ? (
        <RelativeOptions
          {...dateRange}
          presets={presets}
          timezone={timezone}
          units={relativeUnits}
          onSave={handleChange}
        />
      ) : (
        <CustomOptions
          {...dateRange}
          timezone={timezone}
          hideEnd={isRealTime}
          onSave={handleChange}
          onSaveCancel={onChangeCancel}
        />
      )}
    </div>
  );
});

export default DrpPopover;
